import { useState } from 'react'
import { Button, Form, Input, Select, DatePicker, Tag, Popconfirm, InputNumber, App } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { serviceEntriesApi, workOrdersApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import { suppliersApi } from '@/features/gate/api'
import type { ServiceEntry } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const STATUS_COLORS: Record<string, string> = { PENDING: 'orange', APPROVED: 'green', REJECTED: 'red' }

export default function ServiceEntryPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()

  const canCreate = usePermission('service_entry.create')
  const canApprove = usePermission('service_entry.approve')

  const { data, isLoading } = useQuery({
    queryKey: ['service-entries', plantFilter],
    queryFn: () => serviceEntriesApi.list({ plant_id: plantFilter }),
  })
  const { data: plantsData } = useQuery({ queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }) })
  const { data: wos } = useQuery({ queryKey: ['wos-active'], queryFn: () => workOrdersApi.list({ status: 'IN_PROGRESS' }) })
  const { data: suppliers } = useQuery({ queryKey: ['suppliers-all'], queryFn: () => suppliersApi.list({ limit: 500 }) })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const woOptions = wos?.map(w => ({ label: `${w.wo_no} — ${w.supplier_name}`, value: w.id })) ?? []
  const supplierOptions = suppliers?.items?.map((s: any) => ({ label: s.supplier_name, value: s.id })) ?? []

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const payload = {
        ...values,
        service_period_from: dayjs(values.service_period_from as string).format('YYYY-MM-DD'),
        service_period_to: dayjs(values.service_period_to as string).format('YYYY-MM-DD'),
      }
      return serviceEntriesApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service-entries'] }); message.success('Service Entry created'); setDrawerOpen(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const approveMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      serviceEntriesApi.approve(id, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service-entries'] }); message.success('Updated') },
    onError: () => message.error('Failed'),
  })

  const openCreate = () => {
    form.resetFields()
    form.setFieldsValue({ uom: 'DAY', tax_pct: 18 })
    setDrawerOpen(true)
  }

  const columns = [
    { title: 'SE No', dataIndex: 'se_no', key: 'se_no', width: 110 },
    { title: 'WO No', dataIndex: 'wo_no', key: 'wo_no', width: 110 },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name' },
    { title: 'Period From', dataIndex: 'service_period_from', key: 'service_period_from', width: 110 },
    { title: 'Period To', dataIndex: 'service_period_to', key: 'service_period_to', width: 110 },
    { title: 'Total', dataIndex: 'total', key: 'total', width: 110,
      render: (v: string) => `₹${Number(v).toLocaleString('en-IN')}` },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v: string) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 160,
      render: (_: unknown, r: ServiceEntry) =>
        r.status === 'PENDING' && canApprove ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <Popconfirm title="Approve?" onConfirm={() => approveMutation.mutate({ id: r.id, status: 'APPROVED' })}>
              <Button size="small" type="primary">Approve</Button>
            </Popconfirm>
            <Popconfirm title="Reject?" onConfirm={() => approveMutation.mutate({ id: r.id, status: 'REJECTED' })}>
              <Button size="small" danger>Reject</Button>
            </Popconfirm>
          </div>
        ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Service Entry"
        subtitle="Record services completed against Work Orders"
        extra={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New SE</Button>}
      />

      <div style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title="New Service Entry"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending}
        form={form}
      >
        <Form.Item name="wo_id" label="Work Order" rules={[{ required: true }]}>
          <Select options={woOptions} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="supplier_id" label="Supplier" rules={[{ required: true }]}>
          <Select options={supplierOptions} showSearch optionFilterProp="label" />
        </Form.Item>
        <div style={{ display: 'flex', gap: 8 }}>
          <Form.Item name="service_period_from" label="Period From" rules={[{ required: true }]} style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="service_period_to" label="Period To" rules={[{ required: true }]} style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </div>
        <Form.Item name="description" label="Description" rules={[{ required: true }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <div style={{ display: 'flex', gap: 8 }}>
          <Form.Item name="quantity_completed" label="Qty Completed" rules={[{ required: true }]} style={{ flex: 1 }}>
            <InputNumber style={{ width: '100%' }} min={0.001} />
          </Form.Item>
          <Form.Item name="uom" label="UOM" rules={[{ required: true }]} style={{ flex: 1 }}>
            <Input />
          </Form.Item>
          <Form.Item name="rate" label="Rate (₹)" rules={[{ required: true }]} style={{ flex: 1 }}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="tax_pct" label="GST %" style={{ flex: 1 }}>
            <InputNumber style={{ width: '100%' }} min={0} max={28} />
          </Form.Item>
        </div>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
